(function () {
	if (window.__tooltipManager) {
		window.__tooltipManager.init();
		return;
	}

	const SELECTOR = ".tooltip-trigger";
	const TOOLTIP_ID = "tooltip";
	const TOOLTIP_CLASS = "ld-tooltip";
	const HIDE_DELAY = 150;
	const ALLOWED_TAGS = new Set(["B", "STRONG", "EM", "I", "BR"]);
	const padding = 10;
	const gap = 10;
	const opposite = { top: "bottom", bottom: "top", left: "right", right: "left" };

	let activeTooltip = null;
	let tooltip = null;
	let hideTimer = null;
	let tipObserver = null;
	let renderedFor = "";
	let isOpen = false;
	let lastInput = "mouse"; // "mouse" | "touch" | "pen" | "keyboard"

	const isHoverInput = () => lastInput === "mouse";

	// ---- Content (only a few safe tags + **bold** shorthand) ----
	function cleanNode(node) {
		Array.from(node.childNodes).forEach((child) => {
			if (child.nodeType === 1) {
				if (!ALLOWED_TAGS.has(child.tagName)) {
					child.replaceWith(document.createTextNode(child.textContent));
				} else {
					Array.from(child.attributes).forEach((a) => child.removeAttribute(a.name));
					cleanNode(child);
				}
			} else if (child.nodeType !== 3) {
				child.remove();
			}
		});
	}

	function setContent(el, raw) {
		const tpl = document.createElement("template"); // inert: nothing executes or loads
		tpl.innerHTML = String(raw).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
		cleanNode(tpl.content);
		el.replaceChildren(tpl.content);
	}

	// ---- Tooltip element (reuses existing #tooltip) ----
	function getTooltipEl() {
		if (tooltip && document.body.contains(tooltip)) return tooltip;

		tooltip = document.getElementById(TOOLTIP_ID);
		if (!tooltip) {
			tooltip = document.createElement("div");
			tooltip.id = TOOLTIP_ID;
			tooltip.setAttribute("role", "tooltip");
			tooltip.hidden = true;
		}

		tooltip.classList.add(TOOLTIP_CLASS);
		// keep it directly in <body> so position: fixed isn't affected by transformed parents
		if (tooltip.parentNode !== document.body) document.body.appendChild(tooltip);

		return tooltip;
	}

	// Removes duplicates of #tooltip only, never the first one
	function removeStrayTooltips() {
		document.querySelectorAll("#" + TOOLTIP_ID).forEach((n, i) => {
			if (i > 0) n.remove();
		});
	}

	// ---- Guard: if other (old) code overwrites our content or hides the tooltip, restore it ----
	function guardContent() {
		const el = getTooltipEl();
		if (tipObserver) tipObserver.disconnect();

		const options = {
			childList: true,
			characterData: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["hidden"],
		};

		tipObserver = new MutationObserver(() => {
			if (!isOpen || !activeTooltip) return;

			tipObserver.disconnect(); // avoid reacting to our own changes

			// 1) Something else hid the tooltip while we want it open
			if (el.hidden) {
				el.hidden = false;
			}

			// 2) Something replaced our formatted content with raw text
			const hasTags = /<\/?(b|strong|em|i|br)\s*\/?>|\*\*/i.test(renderedFor);
			if (hasTags && !el.querySelector("strong, b, em, i, br")) {
				setContent(el, renderedFor);
			}

			positionFloating(activeTooltip, el);
			tipObserver.observe(el, options);
		});

		tipObserver.observe(el, options);
	}

	// ---- Show / hide ----
	function cancelHide() {
		clearTimeout(hideTimer);
		hideTimer = null;
	}

	function scheduleHide() {
		cancelHide();
		hideTimer = setTimeout(hideTooltip, HIDE_DELAY);
	}

	function showTooltip(trigger) {
		cancelHide();
		const el = getTooltipEl();

		if (activeTooltip && activeTooltip !== trigger) {
			activeTooltip.removeAttribute("aria-expanded");
		}

		activeTooltip = trigger;
		isOpen = true;
		renderedFor = trigger.dataset.tooltip || "";
		setContent(el, renderedFor);
		el.hidden = false;
		positionFloating(trigger, el);
		trigger.setAttribute("aria-expanded", "true");
	}

	function hideTooltip() {
		cancelHide();
		isOpen = false; // set first so the guard doesn't fight us
		const el = getTooltipEl();
		el.hidden = true;
		if (activeTooltip) activeTooltip.removeAttribute("aria-expanded");
		activeTooltip = null;
	}

	// ---- Positioning ----
	function positionFloating(trigger, floating) {
		const triggerRect = trigger.getBoundingClientRect();
		const floatingRect = floating.getBoundingClientRect();

		let placement = trigger.dataset.placement || "top";
		let position = rawPosition(triggerRect, floatingRect, placement);

		const currentSide = parts(placement).side;
		const currentOverflow = sideOverflow(position, floatingRect, currentSide);

		if (currentOverflow > 0) {
			const { align } = parts(placement);
			const flippedSide = opposite[currentSide];
			const flippedPlacement = align === "center" ? flippedSide : `${flippedSide}-${align}`;
			const flippedPosition = rawPosition(triggerRect, floatingRect, flippedPlacement);
			const flippedOverflow = sideOverflow(flippedPosition, floatingRect, flippedSide);

			if (flippedOverflow < currentOverflow) {
				placement = flippedPlacement;
				position = flippedPosition;
			}
		}

		position.x = Math.max(padding, Math.min(position.x, window.innerWidth - floatingRect.width - padding));
		position.y = Math.max(padding, Math.min(position.y, window.innerHeight - floatingRect.height - padding));

		floating.style.left = `${Math.round(position.x)}px`;
		floating.style.top = `${Math.round(position.y)}px`;
	}

	function parts(placement) {
		const [side, align = "center"] = placement.split("-");
		return { side, align };
	}

	function rawPosition(triggerRect, floatingRect, placement) {
		const { side, align } = parts(placement);
		let x = triggerRect.left + triggerRect.width / 2 - floatingRect.width / 2;
		let y = triggerRect.top + triggerRect.height / 2 - floatingRect.height / 2;

		if (side === "top") y = triggerRect.top - floatingRect.height - gap;
		if (side === "bottom") y = triggerRect.bottom + gap;
		if (side === "left") x = triggerRect.left - floatingRect.width - gap;
		if (side === "right") x = triggerRect.right + gap;

		if (side === "top" || side === "bottom") {
			if (align === "start") x = triggerRect.left;
			if (align === "end") x = triggerRect.right - floatingRect.width;
		}
		if (side === "left" || side === "right") {
			if (align === "start") y = triggerRect.top;
			if (align === "end") y = triggerRect.bottom - floatingRect.height;
		}
		return { x, y };
	}

	function sideOverflow(position, rect, side) {
		if (side === "top") return Math.max(0, padding - position.y);
		if (side === "bottom") return Math.max(0, position.y + rect.height - window.innerHeight + padding);
		if (side === "left") return Math.max(0, padding - position.x);
		return Math.max(0, position.x + rect.width - window.innerWidth + padding);
	}

	// ---- Delegated handlers ----
	const closestTrigger = (e) => e.target.closest && e.target.closest(SELECTOR);

	const handlers = {
		// Track last input type (capture phase so it runs first)
		pointerdown(e) {
			lastInput = e.pointerType || "mouse";

			// Touch/pen: tap outside the trigger and tooltip closes it
			if (lastInput !== "mouse" && activeTooltip) {
				const insideTip = getTooltipEl().contains(e.target);
				const onTrigger = closestTrigger(e);
				if (!insideTip && !onTrigger) hideTooltip();
			}
		},
		keydown(e) {
			lastInput = "keyboard";
			if (e.key === "Escape" && activeTooltip) hideTooltip();
		},

		// Mouse hover (ignored for touch, since browsers emulate these after a tap)
		over(e) {
			if (!isHoverInput()) return;
			const t = closestTrigger(e);
			if (t) showTooltip(t);
		},
		out(e) {
			if (!isHoverInput()) return;
			const t = closestTrigger(e);
			if (t && !t.contains(e.relatedTarget)) scheduleHide();
		},

		// Keyboard focus only
		focusin(e) {
			if (lastInput !== "keyboard") return;
			const t = closestTrigger(e);
			if (t) showTooltip(t);
		},
		focusout(e) {
			if (lastInput !== "keyboard") return;
			if (closestTrigger(e)) scheduleHide();
		},

		// Tap/click toggle for touch and pen
		click(e) {
			const t = closestTrigger(e);
			if (!t) return;
			e.preventDefault();  // stop parent <a> navigation / form submit
			e.stopPropagation();
			if (isHoverInput()) return; // mouse already handled by hover
			if (activeTooltip === t && isOpen) hideTooltip();
			else showTooltip(t);
		},

		reposition() {
			if (!activeTooltip) return;
			if (!document.body.contains(activeTooltip)) hideTooltip();
			else positionFloating(activeTooltip, getTooltipEl());
		},

		// Hovering the tooltip itself keeps it open (mouse only)
		tipEnter() {
			if (isHoverInput()) cancelHide();
		},
		tipLeave(e) {
			if (!isHoverInput()) return;
			if (activeTooltip && activeTooltip.contains(e.relatedTarget)) return;
			scheduleHide();
		},

		// Custom events
		customShow(e) {
			const t = closestTrigger(e);
			if (t) showTooltip(t);
		},
		customHide() {
			hideTooltip();
		},
		customInit() {
			init();
		},
	};

	function unbind() {
		document.removeEventListener("pointerdown", handlers.pointerdown, true);
		document.removeEventListener("keydown", handlers.keydown, true);
		document.removeEventListener("mouseover", handlers.over);
		document.removeEventListener("mouseout", handlers.out);
		document.removeEventListener("focusin", handlers.focusin);
		document.removeEventListener("focusout", handlers.focusout);
		document.removeEventListener("click", handlers.click, true);
		document.removeEventListener("tooltip:show", handlers.customShow);
		document.removeEventListener("tooltip:hide", handlers.customHide);
		window.removeEventListener("scroll", handlers.reposition, true);
		window.removeEventListener("resize", handlers.reposition);
		window.removeEventListener("orientationchange", handlers.reposition);
		const el = getTooltipEl();
		el.removeEventListener("mouseenter", handlers.tipEnter);
		el.removeEventListener("mouseleave", handlers.tipLeave);
	}

	function bind() {
		document.addEventListener("pointerdown", handlers.pointerdown, true);
		document.addEventListener("keydown", handlers.keydown, true);
		document.addEventListener("mouseover", handlers.over);
		document.addEventListener("mouseout", handlers.out);
		document.addEventListener("focusin", handlers.focusin);
		document.addEventListener("focusout", handlers.focusout);
		document.addEventListener("click", handlers.click, true);
		document.addEventListener("tooltip:show", handlers.customShow);
		document.addEventListener("tooltip:hide", handlers.customHide);
		window.addEventListener("scroll", handlers.reposition, true);
		window.addEventListener("resize", handlers.reposition);
		window.addEventListener("orientationchange", handlers.reposition);
		const el = getTooltipEl();
		el.addEventListener("mouseenter", handlers.tipEnter);
		el.addEventListener("mouseleave", handlers.tipLeave);
	}

	function init() {
		removeStrayTooltips();
		tooltip = null;
		unbind();
		hideTooltip();
		bind();
		guardContent();
	}

	window.removeEventListener("tooltips:init", handlers.customInit);
	window.addEventListener("tooltips:init", handlers.customInit);

	window.__tooltipManager = { init, show: showTooltip, hide: hideTooltip };

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();